import { GetLayers } from "@actions/surveymanager";
import { SURVEY_WFS_LAYER_SOURCE, type SurveyLayerSource } from 'types/layerRefresh';

export interface SurveyManagerTreeNode {
  key: string;
  title: string;
  children?: SurveyManagerTreeNode[];
  isLeaf?: boolean;
  endPoint?: string;
  endpointPending?: boolean;
  category?: string;
  layerSource?: SurveyLayerSource;
}

const titleCollator = new Intl.Collator(undefined, {
  sensitivity: 'base',
  numeric: true,
});

const sortTreeNodesAlphabetically = (nodes: SurveyManagerTreeNode[]): SurveyManagerTreeNode[] =>
  nodes
    .map((node) => ({
      ...node,
      children: node.children ? sortTreeNodesAlphabetically(node.children) : undefined,
    }))
    .sort((a, b) => titleCollator.compare(a.title, b.title));

export const getSurveyManagerStructure = (
  layers: GetLayers,
  shapefilePathMap: Record<string, Record<string, string | undefined>>,
): SurveyManagerTreeNode[] => {

  const surveyTypeMap = new Map<string, Map<string, { filePath?: string }>>();

  for (const { surveyType = "Unknown", year } of layers.layers) {
    const lcType = surveyType.toLowerCase();
    if (!surveyTypeMap.has(surveyType)) surveyTypeMap.set(surveyType, new Map());
    const yearMap = surveyTypeMap.get(surveyType)!;

    // Deduplicate by surveyType + surveyYear (keep first seen)
    if (!yearMap.has(year)) {
      const filePath = shapefilePathMap[lcType]?.[year];
      yearMap.set(year, { filePath });
    }
  }

  // Build tree structure and enforce alphabetical ordering at every level.
  const nodes = Array.from(surveyTypeMap.entries()).map(([surveyType, yearMap]) => ({
    key: surveyType.toLowerCase(),
    title: surveyType,
    children: Array.from(yearMap.entries()).map(([year, shapefile]) => {
      const typeKey = surveyType.toLowerCase();
      const typePaths = shapefilePathMap[typeKey] ?? {};
      const endpointResolved = Object.prototype.hasOwnProperty.call(typePaths, year);
      return {
        key: `${typeKey}-${year}`,
        title: `${String(year).replace('-', '/')}`,
        isLeaf: true,
        category: surveyType,
        layerSource: SURVEY_WFS_LAYER_SOURCE,
        endPoint: shapefile.filePath
          ? `${shapefile.filePath}&outputFormat=application%2Fjson`
          : undefined,
        endpointPending: !endpointResolved,
      };
    }),
  }));

  return sortTreeNodesAlphabetically(nodes);
};
